<template>
    <Head title="Checklist Performance Analytics" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Header -->
        <div class="mb-8">
            <div class="flex items-center justify-between">
                <div>
                    <Link
                        :href="route('dashboard')"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-4"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Dashboard
                    </Link>
                    <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
                        Checklist Performance Analytics
                    </h1>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Comprehensive performance metrics and insights for your inspection & checklist operations
                    </p>
                </div>
                <button
                    v-if="!loading && data"
                    @click="exportPdf"
                    :disabled="exporting"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-500 dark:hover:bg-primary-600"
                >
                    <svg v-if="!exporting" class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <svg v-else class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ exporting ? 'Exporting...' : 'Export PDF' }}
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div v-for="i in 4" :key="i" class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
                    <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                    <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div v-else-if="data" class="space-y-8">
            <!-- Time Period Cards -->
            <div>
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Overview</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <!-- Today Card -->
                    <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg shadow-lg p-6 border border-blue-200 dark:border-blue-800">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-sm font-medium text-blue-900 dark:text-blue-300">{{ data.periods.today.label }}</h3>
                            <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="space-y-2">
                            <div class="flex items-end justify-between">
                                <span class="text-3xl font-bold text-blue-900 dark:text-blue-100">{{ data.periods.today.total }}</span>
                                <span class="text-sm text-blue-700 dark:text-blue-300">Total</span>
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <span class="text-blue-700 dark:text-blue-400">Completed:</span>
                                    <span class="font-semibold text-blue-900 dark:text-blue-200 ml-1">{{ data.periods.today.completed }}</span>
                                </div>
                                <div>
                                    <span class="text-blue-700 dark:text-blue-400">In Progress:</span>
                                    <span class="font-semibold text-blue-900 dark:text-blue-200 ml-1">{{ data.periods.today.inProgress }}</span>
                                </div>
                            </div>
                            <div class="pt-2 border-t border-blue-200 dark:border-blue-700">
                                <div class="flex justify-between text-xs">
                                    <span class="text-blue-700 dark:text-blue-400">Completion Rate</span>
                                    <span class="font-bold text-blue-900 dark:text-blue-200">{{ data.periods.today.completionRate }}%</span>
                                </div>
                                <div class="flex justify-between text-xs mt-1">
                                    <span class="text-blue-700 dark:text-blue-400">SLA Compliance</span>
                                    <span class="font-bold text-blue-900 dark:text-blue-200">{{ data.periods.today.slaCompliance }}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 7 Days Card -->
                    <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg shadow-lg p-6 border border-purple-200 dark:border-purple-800">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-sm font-medium text-purple-900 dark:text-purple-300">{{ data.periods['7days'].label }}</h3>
                            <svg class="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <div class="space-y-2">
                            <div class="flex items-end justify-between">
                                <span class="text-3xl font-bold text-purple-900 dark:text-purple-100">{{ data.periods['7days'].total }}</span>
                                <span class="text-sm text-purple-700 dark:text-purple-300">Total</span>
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <span class="text-purple-700 dark:text-purple-400">Completed:</span>
                                    <span class="font-semibold text-purple-900 dark:text-purple-200 ml-1">{{ data.periods['7days'].completed }}</span>
                                </div>
                                <div>
                                    <span class="text-purple-700 dark:text-purple-400">Overdue:</span>
                                    <span class="font-semibold text-purple-900 dark:text-purple-200 ml-1">{{ data.periods['7days'].overdue }}</span>
                                </div>
                            </div>
                            <div class="pt-2 border-t border-purple-200 dark:border-purple-700">
                                <div class="flex justify-between text-xs">
                                    <span class="text-purple-700 dark:text-purple-400">Avg Completion</span>
                                    <span class="font-bold text-purple-900 dark:text-purple-200">{{ data.periods['7days'].avgCompletionTimeFormatted || 'N/A' }}</span>
                                </div>
                                <div class="flex justify-between text-xs mt-1">
                                    <span class="text-purple-700 dark:text-purple-400">Avg Response</span>
                                    <span class="font-bold text-purple-900 dark:text-purple-200">{{ data.periods['7days'].avgResponseTimeFormatted || 'N/A' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 30 Days Card -->
                    <div class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg shadow-lg p-6 border border-amber-200 dark:border-amber-800">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-sm font-medium text-amber-900 dark:text-amber-300">{{ data.periods['30days'].label }}</h3>
                            <svg class="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div class="space-y-2">
                            <div class="flex items-end justify-between">
                                <span class="text-3xl font-bold text-amber-900 dark:text-amber-100">{{ data.periods['30days'].total }}</span>
                                <span class="text-sm text-amber-700 dark:text-amber-300">Total</span>
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <span class="text-amber-700 dark:text-amber-400">Completed:</span>
                                    <span class="font-semibold text-amber-900 dark:text-amber-200 ml-1">{{ data.periods['30days'].completed }}</span>
                                </div>
                                <div>
                                    <span class="text-amber-700 dark:text-amber-400">On Time:</span>
                                    <span class="font-semibold text-amber-900 dark:text-amber-200 ml-1">{{ data.periods['30days'].onTime }}</span>
                                </div>
                            </div>
                            <div class="pt-2 border-t border-amber-200 dark:border-amber-700">
                                <div class="flex justify-between text-xs">
                                    <span class="text-amber-700 dark:text-amber-400">Completion Rate</span>
                                    <span class="font-bold text-amber-900 dark:text-amber-200">{{ data.periods['30days'].completionRate }}%</span>
                                </div>
                                <div class="flex justify-between text-xs mt-1">
                                    <span class="text-amber-700 dark:text-amber-400">SLA Compliance</span>
                                    <span class="font-bold text-amber-900 dark:text-amber-200">{{ data.periods['30days'].slaCompliance }}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 180 Days Card -->
                    <div class="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg shadow-lg p-6 border border-emerald-200 dark:border-emerald-800">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-sm font-medium text-emerald-900 dark:text-emerald-300">{{ data.periods['180days'].label }}</h3>
                            <svg class="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div class="space-y-2">
                            <div class="flex items-end justify-between">
                                <span class="text-3xl font-bold text-emerald-900 dark:text-emerald-100">{{ data.periods['180days'].total }}</span>
                                <span class="text-sm text-emerald-700 dark:text-emerald-300">Total</span>
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <span class="text-emerald-700 dark:text-emerald-400">Completed:</span>
                                    <span class="font-semibold text-emerald-900 dark:text-emerald-200 ml-1">{{ data.periods['180days'].completed }}</span>
                                </div>
                                <div>
                                    <span class="text-emerald-700 dark:text-emerald-400">In Progress:</span>
                                    <span class="font-semibold text-emerald-900 dark:text-emerald-200 ml-1">{{ data.periods['180days'].inProgress }}</span>
                                </div>
                            </div>
                            <div class="pt-2 border-t border-emerald-200 dark:border-emerald-700">
                                <div class="flex justify-between text-xs">
                                    <span class="text-emerald-700 dark:text-emerald-400">Avg Completion</span>
                                    <span class="font-bold text-emerald-900 dark:text-emerald-200">{{ data.periods['180days'].avgCompletionTimeFormatted || 'N/A' }}</span>
                                </div>
                                <div class="flex justify-between text-xs mt-1">
                                    <span class="text-emerald-700 dark:text-emerald-400">SLA Compliance</span>
                                    <span class="font-bold text-emerald-900 dark:text-emerald-200">{{ data.periods['180days'].slaCompliance }}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Key Insights -->
            <div v-if="data.insights && data.insights.length > 0" class="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 rounded-lg shadow-lg p-6 border border-indigo-200 dark:border-indigo-800">
                <div class="flex items-center mb-4">
                    <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Key Insights</h2>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div
                        v-for="(insight, index) in data.insights"
                        :key="index"
                        :class="[
                            'p-4 rounded-lg border-l-4',
                            insight.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-500' : '',
                            insight.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500' : '',
                            insight.type === 'info' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500' : '',
                        ]"
                    >
                        <p :class="[
                            'text-sm',
                            insight.type === 'success' ? 'text-green-800 dark:text-green-300' : '',
                            insight.type === 'warning' ? 'text-amber-800 dark:text-amber-300' : '',
                            insight.type === 'info' ? 'text-blue-800 dark:text-blue-300' : '',
                        ]">
                            {{ insight.message }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Charts Grid -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Status Distribution -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Status Distribution (Last 30 Days)</h3>
                    <div class="w-full overflow-hidden">
                        <PieChart
                            :data="data.charts.statusDistribution.data"
                            :colors="data.charts.statusDistribution.colors"
                            :height="420"
                            :legendCols="4"
                        />
                    </div>
                </div>

                <!-- SLA Compliance -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">SLA Compliance (Last 30 Days)</h3>
                    <div class="w-full overflow-hidden">
                        <BarChart
                            :data="data.charts.slaCompliance"
                            :height="350"
                        />
                    </div>
                </div>

                <!-- Daily Performance -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Daily Performance (Last 7 Days)</h3>
                    <div class="w-full overflow-hidden">
                        <BarChart
                            :data="data.charts.dailyPerformance"
                            :height="350"
                        />
                    </div>
                </div>

                <!-- Completion Trend -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Completion Rate Trend (Last 30 Days)</h3>
                    <div class="w-full overflow-hidden">
                        <LineChart
                            :data="data.charts.completionTrend"
                            :height="350"
                        />
                    </div>
                </div>
            </div>

            <!-- User Performance - Top Performers -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top 5 Performers (Last 30 Days)</h3>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="overflow-hidden">
                        <BarChart
                            :data="data.userPerformance.chartData"
                            :height="350"
                        />
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">On-Time</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr v-for="performer in data.userPerformance.topPerformers" :key="performer.name">
                                    <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ performer.name }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ performer.total }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ performer.completed }} ({{ performer.completionRate }}%)</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ performer.onTimeRate }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- User Performance - Bottom Performers -->
            <div v-if="data.userPerformance.bottomPerformers && data.userPerformance.bottomPerformers.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lowest 5 Underperformers (Last 30 Days)</h3>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="overflow-hidden">
                        <BarChart
                            :data="data.userPerformance.bottomChartData"
                            :height="350"
                        />
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">On-Time</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr v-for="performer in data.userPerformance.bottomPerformers" :key="performer.name">
                                    <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ performer.name }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ performer.total }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ performer.completed }} ({{ performer.completionRate }}%)</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ performer.onTimeRate }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Location Performance - Top 5 -->
            <div v-if="data.locationPerformance.locations.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top 5 Locations by Performance (Last 30 Days)</h3>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="overflow-hidden">
                        <BarChart
                            :data="data.locationPerformance.chartData"
                            :height="350"
                        />
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">On-Time</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr v-for="location in data.locationPerformance.locations" :key="location.name">
                                    <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ location.name }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ location.total }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ location.completed }} ({{ location.completionRate }}%)</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ location.onTimeRate }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Location Performance - Bottom 5 -->
            <div v-if="data.locationPerformance.bottomLocations && data.locationPerformance.bottomLocations.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lowest 5 Locations by Performance (Last 30 Days)</h3>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="overflow-hidden">
                        <BarChart
                            :data="data.locationPerformance.bottomChartData"
                            :height="350"
                        />
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">On-Time</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr v-for="location in data.locationPerformance.bottomLocations" :key="location.name">
                                    <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ location.name }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ location.total }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ location.completed }} ({{ location.completionRate }}%)</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ location.onTimeRate }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Workflow Efficiency -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 overflow-hidden">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Workflow Efficiency - Average Time per Stage (Last 30 Days)</h3>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="overflow-hidden">
                        <BarChart
                            :data="data.workflowEfficiency.chartData"
                            :height="300"
                            :horizontal="true"
                        />
                    </div>
                    <div class="space-y-4">
                        <div
                            v-for="(time, stage) in data.workflowEfficiency.stages"
                            :key="stage"
                            class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ stage }}</span>
                            <div class="text-right">
                                <span class="text-lg font-bold text-primary-600 dark:text-primary-400">{{ formatMinutes(time) }}</span>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ time }} minutes</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">Failed to load data</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Please try refreshing the page</p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';
import BarChart from '@/Components/Charts/BarChart.vue';
import LineChart from '@/Components/Charts/LineChart.vue';
import PieChart from '@/Components/Charts/PieChart.vue';
import route from '@/Utils/route';

const props = defineProps({
    pageTitle: String,
});

const toast = useToast();
const axios = useSecureAxios();

const loading = ref(true);
const exporting = ref(false);
const data = ref(null);

const loadPerformanceData = async () => {
    loading.value = true;
    try {
        const response = await axios.get(route('checklists.performance.load'));
        if (response.data.success) {
            data.value = response.data;
        } else {
            toast.error('Failed to load performance data');
        }
    } catch (error) {
        console.error('Failed to load performance data:', error);
        toast.error('Failed to load performance data');
    } finally {
        loading.value = false;
    }
};

const exportPdf = async () => {
    exporting.value = true;
    try {
        const response = await axios.post(route('checklists.performance.export-pdf'), {
            data: data.value,
        }, {
            responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `checklist-performance-${date}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('PDF exported successfully');
    } catch (error) {
        console.error('Failed to export PDF:', error);
        toast.error('Failed to export PDF');
    } finally {
        exporting.value = false;
    }
};

const formatMinutes = (minutes) => {
    if (!minutes) return 'N/A';

    if (minutes < 60) {
        return `${Math.round(minutes)} min`;
    }

    if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins > 0 ? mins + 'm' : ''}`;
    }

    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days}d ${hours > 0 ? hours + 'h' : ''}`;
};

onMounted(() => {
    loadPerformanceData();
});
</script>
